import { makeAutoObservable, runInAction } from 'mobx'

import { notificationService, restClient } from '@/Core'
import { crudResourceRegistry } from '@/Core/Services/CrudResourceRegistry'
import { GetResourceTask } from '@/Core/Tasks/Resources/GetResourceTask'
import { serverMessage, serverStatus } from '@/Ship/Helpers/serverError'

import { RunTransportRequestTransitionTask } from './RunTransportRequestTransitionTask'
import { TRANSPORT_REQUEST_TEXTS as T } from './TransportRequest.types'

import type { LossReasonOption } from './LossReasonModal'
import type {
    TransportRequestActionsConfig,
    TransportRequestCrudContextLike,
    TransportRequestPermissions,
    TransportRequestTransition,
} from './TransportRequest.types'

/** Порядок кнопок у шапці картки — як у легасі: конвертація, програш, повернення. */
export const TRANSPORT_REQUEST_TRANSITIONS: readonly TransportRequestTransition[] = ['convert', 'lose', 'return_to_work']

/**
 * Статуси, після яких картка вже не та, що на екрані: 400 — переходу з
 * поточного стану вже немає (запис змінили раніше), 403 — права змінилися,
 * 404 — запис видалено. Картку тоді перечитуємо, щоб кнопки показали правду.
 */
const STALE_CARD_STATUSES = new Set([400, 403, 404])

/** Найдовший коментар програшу — як у інших коментарів CRM (`comment` ≤ 4096). */
export const LOSS_COMMENT_MAX_LENGTH = 4096

interface Violation {
    field?: string
    propertyPath?: string
    message?: string
}

const violationsOf = (error: unknown): Violation[] => {
    const response = (error as { response?: { violations?: unknown } } | null)?.response
    const list = response?.violations

    return Array.isArray(list) ? (list as Violation[]) : []
}

/** Причини програшу з довідника так, як їх віддає колекція. */
interface LossReasonRow {
    uuid?: string
    title?: string
}

/**
 * Переходи однієї картки запиту чи тендера (`SPEC-022` §8.4):
 *
 *  - **«Програти»** — модалка з причиною (обов'язкова; 422 — під полем);
 *  - **«Повернути в роботу»** — одразу, без підтвердження, як у легасі;
 *  - **«Конвертація в заявку»** — Hook заявки з конфігу власника.
 *
 * Після успіху картка бере нову версію з відповіді (статус, `_permissions`),
 * а список під шухлядою перечитується — разом із ним і плашки статусів.
 */
export class TransportRequestActionsStore {
    pending: TransportRequestTransition | null = null

    loseOpen = false
    lossReason: string | undefined = undefined
    lossComment = ''
    lossReasonError: string | null = null
    loseError: string | null = null

    reasons: LossReasonOption[] = []
    reasonsLoading = false

    constructor(
        readonly config: TransportRequestActionsConfig,
        private crudContext: TransportRequestCrudContextLike
    ) {
        makeAutoObservable<TransportRequestActionsStore, 'crudContext'>(this, { config: false, crudContext: false })
    }

    /** Ядро створює `crudContext` картки наново — стор лишається, контекст оновлюється. */
    bind(crudContext: TransportRequestCrudContextLike): void {
        this.crudContext = crudContext
    }

    get permissions(): TransportRequestPermissions {
        const permissions = this.crudContext.formContext?.values?._permissions

        return permissions !== null && typeof permissions === 'object' ? permissions : {}
    }

    /** Кнопки, які варто показати: лише в перегляді й лише дозволені `_permissions`. */
    allowed(isEditable: boolean | undefined): TransportRequestTransition[] {
        if (isEditable === true) return []

        const permissions = this.permissions

        return TRANSPORT_REQUEST_TRANSITIONS.filter(
            (transition) =>
                permissions[transition] === true && (transition !== 'convert' || this.config.convert !== undefined)
        )
    }

    start(transition: TransportRequestTransition): void {
        if (this.pending !== null) return

        if (transition === 'lose') {
            this.openLose()
            return
        }

        if (transition === 'return_to_work') {
            void this.run('return_to_work', {})
            return
        }

        const values = this.crudContext.formContext?.values

        if (values && this.config.convert) {
            this.config.convert(values, () => {
                void this.reloadCard()
                this.reloadList()
            })
        }
    }

    setLossReason(value: string | undefined): void {
        this.lossReason = value
        this.lossReasonError = null
    }

    setLossComment(value: string): void {
        this.lossComment = value
    }

    closeLose(): void {
        this.loseOpen = false
        this.loseError = null
        this.lossReasonError = null
    }

    /**
     * Обов'язковість причини перевіряє бек: без неї — 422 під полем, модалка
     * лишається відкритою (С17.1). Текст помилки — серверний, а не копія тут.
     */
    async submitLose(): Promise<void> {
        await this.run('lose', {
            lossReason: this.lossReason ?? null,
            ...(this.config.withComment && this.lossComment.trim() !== '' ? { comment: this.lossComment.trim() } : {}),
        })
    }

    private openLose(): void {
        this.loseOpen = true
        this.lossReason = undefined
        this.lossComment = ''
        this.lossReasonError = null
        this.loseError = null

        if (this.reasons.length === 0) void this.loadReasons()
    }

    private async loadReasons(): Promise<void> {
        const restUrl = GetResourceTask(this.config.lossReasonResource)?.restUrl

        if (typeof restUrl !== 'string' || restUrl === '') return

        this.reasonsLoading = true

        try {
            const raw = (await restClient.get(`${restUrl}?limit=100`, undefined, { forceRefresh: true })) as unknown
            const data = raw as { items?: LossReasonRow[]; 'hydra:member'?: LossReasonRow[] } | null
            const rows = data?.items ?? data?.['hydra:member'] ?? []

            runInAction(() => {
                this.reasons = rows
                    .filter((row): row is LossReasonRow & { uuid: string } => typeof row.uuid === 'string')
                    .map((row) => ({ value: row.uuid, label: typeof row.title === 'string' ? row.title : row.uuid }))
            })
        } catch {
            // Без переліку модалка лишається з порожнім вибором — бек відповість 422
        } finally {
            runInAction(() => {
                this.reasonsLoading = false
            })
        }
    }

    private async run(transition: 'lose' | 'return_to_work', body: Record<string, unknown>): Promise<void> {
        const uuid = this.crudContext.formContext?.values?.uuid

        if (typeof uuid !== 'string' || uuid === '' || this.pending !== null) return

        this.pending = transition

        try {
            const card = await RunTransportRequestTransitionTask(this.config.restUrl, uuid, transition, body)

            runInAction(() => {
                this.pending = null
                this.loseOpen = false
            })

            notificationService.showSuccess(
                T.successTitle,
                transition === 'lose' ? this.config.texts.loseSuccess : this.config.texts.returnSuccess
            )

            await this.applyCard(card)
            this.reloadList()
        } catch (error: unknown) {
            runInAction(() => {
                this.pending = null
            })

            this.handleError(transition, error)
        }
    }

    private handleError(transition: 'lose' | 'return_to_work', error: unknown): void {
        const status = serverStatus(error)

        if (status === 422 && transition === 'lose' && this.loseOpen) {
            const violation = violationsOf(error).find(
                (item) => (item.field ?? item.propertyPath) === 'lossReason' && typeof item.message === 'string'
            )

            if (violation?.message) {
                this.lossReasonError = violation.message
                return
            }
        }

        const message = serverMessage(error, T.contactAdmin)
        const isStale = status !== null && STALE_CARD_STATUSES.has(status)

        // Модалка лишається, лише якщо її ще можна виправити й надіслати знову
        if (!isStale && transition === 'lose' && this.loseOpen) {
            this.loseError = message
            return
        }

        this.closeLose()
        notificationService.showError(T.errorTitle, message)

        if (isStale) {
            void this.reloadCard()
            this.reloadList()
        }
    }

    private async applyCard(card: Record<string, unknown> | null): Promise<void> {
        const form = this.crudContext.formContext
        const permissions = card?.['_permissions']

        if (card && permissions !== null && typeof permissions === 'object' && form?.setInitialValues) {
            form.setInitialValues(card)
            form.setStorage?.('responseData', { ...card })
            return
        }

        await this.reloadCard()
    }

    private async reloadCard(): Promise<void> {
        try {
            await this.crudContext.actions?.reloadData?.()
        } catch {
            // Запис могли видалити — лишається повідомлення сервера, показане вище
        }
    }

    private reloadList(): void {
        const load = this.crudContext.listCrudContext?.listContext?.actions?.load

        if (typeof load === 'function') {
            load(true)
            return
        }

        const key = this.crudContext.resource?.key

        if (typeof key === 'string' && key !== '') crudResourceRegistry.reloadListIfExists(key)
    }
}

const stores = new WeakMap<object, TransportRequestActionsStore>()

/**
 * Стор переходів картки — один на ядровий `crudContext`: кнопки (`onButtons`)
 * і модалка (`content`) беруть той самий екземпляр, бо ядрова `ButtonGroupStore`
 * копіює конфіг кнопок і стан у них жити не може.
 */
export const transportRequestActionsStoreFor = (
    crudContext: TransportRequestCrudContextLike,
    config: TransportRequestActionsConfig
): TransportRequestActionsStore => {
    let store = stores.get(crudContext)

    if (!store) {
        store = new TransportRequestActionsStore(config, crudContext)
        stores.set(crudContext, store)
    } else {
        store.bind(crudContext)
    }

    return store
}
